import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {DeadlineStickerEntry, Hub} from 'yougile-model'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {DeadlineItem, Point, TimelineScale, TimelineScaleData} from './types'

export const hubToDeadlineItem = (
  hub: Hub,
  current?: DeadlineStickerEntry
): DeadlineItem => ({
  deadline: current ?? Sticker.getDeadline(hub)?.current,
  completed: hub.data?.gtd?.state?.stage === 'done'
})

export const recurseSubtasks = (hub: Hub, fn: (hub: Hub) => void): void => {
  const observed = new Set()
  const it = (hub: Hub, fn: (hub: Hub) => void) => {
    if (observed.has(hub.id) || Task.isArchived(hub)) {
      return
    }
    observed.add(hub.id)

    fn(hub)

    const list = SubtaskUtil.getSubtaskList(hub)
    const subtasks = SubtaskUtil.getSubtasksFromListAsHub(list)
    for (const subtask of subtasks) {
      it(subtask, fn)
    }
  }

  it(hub, fn)
}

export const getScaleData = (scale: TimelineScale): TimelineScaleData => {
  switch (scale) {
    case TimelineScale.Days:
      return [Time.day, 40]
    case TimelineScale.Weeks:
      return [Time.day, 24]
    case TimelineScale.Months:
      return [Time.day, 8]
    case TimelineScale.Years:
      return [Time.day, 2]
  }
}

export const getNextScale = (scale: TimelineScale): TimelineScale => {
  switch (scale) {
    case TimelineScale.Days:
      return TimelineScale.Weeks
    case TimelineScale.Weeks:
      return TimelineScale.Months
    case TimelineScale.Months:
      return TimelineScale.Years
    case TimelineScale.Years:
      return TimelineScale.Years
  }
}

export const getPrevScale = (scale: TimelineScale): TimelineScale => {
  switch (scale) {
    case TimelineScale.Days:
      return TimelineScale.Days
    case TimelineScale.Weeks:
      return TimelineScale.Days
    case TimelineScale.Months:
      return TimelineScale.Weeks
    case TimelineScale.Years:
      return TimelineScale.Months
  }
}

export const getScaleTitle = (scale: TimelineScale): string => {
  switch (scale) {
    case TimelineScale.Days:
      return Lang.msg.timeline.scale.days
    case TimelineScale.Weeks:
      return Lang.msg.timeline.scale.weeks
    case TimelineScale.Months:
      return Lang.msg.timeline.scale.months
    case TimelineScale.Years:
      return Lang.msg.timeline.scale.years
  }
}

export const getScaleScrollDelta = (scale: TimelineScale): number => {
  switch (scale) {
    case TimelineScale.Days:
      return Time.day
    case TimelineScale.Weeks:
      return 7 * Time.day
    case TimelineScale.Months:
      return 14 * Time.day
    case TimelineScale.Years:
      return 30 * Time.day
  }
}

export const getScaleBoundsPadding = (scale: TimelineScale): number => {
  switch (scale) {
    case TimelineScale.Days:
      return 30 * Time.day
    case TimelineScale.Weeks:
      return 60 * Time.day
    case TimelineScale.Months:
      return 120 * Time.day
    case TimelineScale.Years:
      return 240 * Time.day
  }
}

// These operate on local time and don't introduce server offset

export const startOfDay = (date: Date): Date => {
  date = new Date(date)
  date.setHours(0, 0, 0, 0)
  return date
}

export const startOfWeek = (date: Date): Date => {
  date = startOfDay(date)
  const dayOffset = date.getDay() ? date.getDay() - 1 : 6
  date.setDate(date.getDate() - dayOffset)
  return date
}

export const startOfMonth = (date: Date): Date => {
  date = startOfDay(date)
  date.setDate(1)
  return date
}

export const startOfYear = (date: Date): Date => {
  date = startOfDay(date)
  date.setMonth(0, 1)
  return date
}

export const applyTimeOffset = (date: Date, reference: Date): void => {
  date.setHours(reference.getHours())
  date.setMinutes(reference.getMinutes())
  date.setSeconds(reference.getSeconds())
  date.setMilliseconds(reference.getMilliseconds())
}

export const distance = (p0: Point, p1: Point): number =>
  Math.sqrt((p1.x - p0.x) ** 2 + (p1.y - p0.y) ** 2)
